import { useState } from 'react';

import { cn } from '@src/design-system/helpers';
import type { TMedia } from '@src/shared/types/media';

import { LivePreviewDevice } from './live-preview-device';
import { MessagePreview } from './message-preview';
import { PreviewModeSwitch, type PreviewMode } from './preview-mode-switch';

export type LivePreviewProps = {
  className?: string;
  channel?: string;
  content?: string;
  subject?: string;
  attachments?: TMedia[];
  defaultMode?: PreviewMode;
  showModeSwitch?: boolean;
};

export type { PreviewMode } from './preview-mode-switch';
export { PreviewModeSwitch } from './preview-mode-switch';
export { MessagePreview } from './message-preview';
export { LivePreviewDevice } from './live-preview-device';

/**
 * Reminder preview with Message / Live Preview toggle.
 * Use anywhere a channel message or device preview is needed.
 */
export function LivePreview({
  className,
  channel,
  content,
  subject,
  attachments,
  defaultMode = 'live',
  showModeSwitch = true,
}: LivePreviewProps) {
  const [mode, setMode] = useState<PreviewMode>(defaultMode);

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-1 flex-col items-center gap-7',
        className
      )}
    >
      {showModeSwitch ? (
        <PreviewModeSwitch
          mode={mode}
          onModeChange={setMode}
          className='w-full'
        />
      ) : null}

      <div className='flex min-h-0 w-full flex-1 flex-col items-center overflow-auto'>
        {showModeSwitch && mode === 'message' ? (
          <MessagePreview
            attachments={attachments}
            channel={channel}
            content={content}
            className='mx-auto w-full'
          />
        ) : null}

        {!showModeSwitch || mode === 'live' ? (
          <LivePreviewDevice
            attachments={attachments}
            channel={channel}
            className='min-h-0 w-full flex-1'
            content={content}
            subject={subject}
          />
        ) : null}
      </div>
    </div>
  );
}
